import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import StatCard from '../components/StatCard';
import StatusWidgets from '../components/StatusWidgets';
import ApprovalQueue from '../components/ApprovalQueue';
import AiInsightCard from '../components/AiInsightCard';
import EmptyState from '../components/EmptyState';
import AppLinkGrid from '../components/AppLinkGrid';
import {
  ChartCard,
  ChartSkeleton,
  BarChart,
  AreaChart,
  DonutChart,
  CHART_THEME,
} from '../components/charts';
import { controlClass } from '../components/FilterBar';
import {
  fetchDashboardSummary,
  fetchOverviewCharts,
  fetchAppLinks,
  syncMercury,
  type ChartPeriod,
  type DashboardAttentionItem,
  type DashboardSummary,
  type OverviewChartsResponse,
} from '../lib/api';
import { getQuickLinkApps, mergeAppLinkUrls } from '../lib/appLinks';
import { formatDateTime } from '../lib/format';

const PERIOD_OPTIONS: { value: ChartPeriod; label: string }[] = [
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '12m', label: '12 months' },
];

const ATTENTION_TONE: Record<DashboardAttentionItem['severity'], string> = {
  info: 'text-zt-muted',
  success: 'text-zt-primary',
  warning: 'text-zt-warning',
  danger: 'text-zt-danger',
};

function StatCardSkeleton() {
  return (
    <div className="rounded-xl border border-zt-border bg-zt-surface p-4">
      <div className="h-8 w-20 animate-pulse rounded bg-zt-border" />
      <div className="mt-2 h-4 w-28 animate-pulse rounded bg-zt-border" />
    </div>
  );
}

function AttentionItem({ item }: { item: DashboardAttentionItem }) {
  const Icon =
    item.severity === 'success'
      ? CheckCircle2
      : item.severity === 'danger'
      ? XCircle
      : AlertCircle;

  const content = (
    <div className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-zt-border bg-zt-bg p-4">
      <div className="flex min-w-0 items-center gap-3">
        <Icon className={`shrink-0 ${ATTENTION_TONE[item.severity]}`} size={20} />
        <span className="min-w-0 truncate text-sm text-slate-200">{item.title}</span>
      </div>
      {item.action && item.href ? (
        <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-zt-primary">
          {item.action} <ArrowRight size={16} />
        </span>
      ) : null}
    </div>
  );

  if (item.href && item.action) {
    return (
      <Link to={item.href} className="block transition-opacity hover:opacity-90">
        {content}
      </Link>
    );
  }

  return content;
}

function Overview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [charts, setCharts] = useState<OverviewChartsResponse | null>(null);
  const [chartsError, setChartsError] = useState<string | null>(null);
  const [period, setPeriod] = useState<ChartPeriod>('90d');
  const [loading, setLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [mercurySyncing, setMercurySyncing] = useState(false);
  const [quickLinkApps, setQuickLinkApps] = useState(getQuickLinkApps());

  useEffect(() => {
    let cancelled = false;
    fetchAppLinks().then((result) => {
      if (!cancelled && result.ok && result.data?.urls) {
        setQuickLinkApps(mergeAppLinkUrls(result.data.urls).filter((link) => link.quickLink));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setSummaryError(null);
      try {
        const dashboard = await fetchDashboardSummary();
        if (cancelled) return;
        if (dashboard.ok && dashboard.data) {
          setSummary(dashboard.data);
          setSummaryError(null);
        } else {
          setSummary(null);
          setSummaryError(dashboard.error?.message ?? 'Could not load dashboard summary');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    const interval = setInterval(load, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadCharts() {
      setChartsLoading(true);
      setChartsError(null);
      const result = await fetchOverviewCharts(period);
      if (!cancelled) {
        if (result.ok && result.data) {
          setCharts(result.data);
          setChartsError(null);
        } else {
          setCharts(null);
          setChartsError(result.error?.message ?? 'Could not load chart data');
        }
        setChartsLoading(false);
      }
    }

    loadCharts();
    return () => {
      cancelled = true;
    };
  }, [period]);

  useEffect(() => {
    if (searchParams.get('focus') === 'approvals') {
      document.getElementById('approval-queue')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [searchParams]);

  const handleMercurySync = useCallback(async () => {
    setMercurySyncing(true);
    try {
      const result = await syncMercury();
      if (result.ok) {
        toast.success('Mercury sync started');
        navigate('/mercury');
      }
    } finally {
      setMercurySyncing(false);
    }
  }, [navigate]);

  const apiStatus = summary?.apiStatus ?? null;
  const attentionItems = summary?.attentionItems ?? [];

  const revenueExpenseData = charts?.revenueExpenses.data ?? [];
  const cashFlowData = useMemo(
    () =>
      (charts?.cashFlow.data ?? []).map((point) => ({
        date: point.date.slice(5),
        cumulative: point.cumulative,
      })),
    [charts?.cashFlow.data]
  );

  const integrationSlices = charts
    ? [
        { name: 'Connected', value: charts.integrationHealth.connected, color: CHART_THEME.connected },
        { name: 'Warning', value: charts.integrationHealth.warning, color: CHART_THEME.warning },
        { name: 'Error', value: charts.integrationHealth.error, color: CHART_THEME.error },
      ].filter((s) => s.value > 0)
    : [];

  return (
    <div className="min-w-0 space-y-6">
      {summaryError && !loading ? (
        <div className="flex items-start gap-3 rounded-xl border border-zt-danger/40 bg-zt-danger/10 px-4 py-3 text-sm text-zt-danger">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Dashboard API unreachable</p>
            <p className="mt-1 break-words text-zt-danger/90">{summaryError}</p>
          </div>
        </div>
      ) : null}

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : summaryError ? (
          <>
            <StatCard label="API status" value="Unavailable" tone="danger" icon={<XCircle size={18} />} />
            <StatCard label="Failed webhooks" value="—" tone="neutral" />
            <StatCard label="Unpaid invoices" value="—" tone="neutral" />
            <StatCard label="Pending approvals" value="—" tone="neutral" />
          </>
        ) : (
          <>
            <StatCard
              label="API status"
              value={apiStatus === 'healthy' ? 'Healthy' : 'Degraded'}
              tone={apiStatus === 'healthy' ? 'success' : 'danger'}
              icon={
                apiStatus === 'healthy' ? (
                  <CheckCircle2 size={18} />
                ) : (
                  <XCircle size={18} />
                )
              }
            />
            <StatCard
              label="Failed webhooks"
              value={String(summary?.failedWebhooks ?? 0)}
              tone={(summary?.failedWebhooks ?? 0) > 0 ? 'warning' : 'success'}
            />
            <StatCard
              label="Unpaid invoices"
              value={
                summary && !summary.unpaidInvoicesConfigured
                  ? '—'
                  : String(summary?.unpaidInvoices ?? 0)
              }
              tone={
                !summary?.unpaidInvoicesConfigured
                  ? 'neutral'
                  : (summary?.unpaidInvoices ?? 0) > 0
                  ? 'warning'
                  : 'success'
              }
            />
            <StatCard
              label="Pending approvals"
              value={String(summary?.pendingApprovals ?? 0)}
              tone={(summary?.pendingApprovals ?? 0) > 0 ? 'warning' : 'success'}
            />
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zt-muted">Operator metrics · session-protected</p>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as ChartPeriod)}
          className={controlClass}
          aria-label="Chart date range"
        >
          {PERIOD_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2">
        <ChartCard
          title="Revenue vs expenses"
          subtitle={
            charts?.revenueExpenses.source === 'erpnext'
              ? 'ERPNext P&L'
              : 'Derived from synced transactions (owner capital excluded from revenue)'
          }
        >
          {chartsLoading ? (
            <ChartSkeleton />
          ) : revenueExpenseData.length === 0 ? (
            <EmptyState
              title="No P&L data"
              message="Connect ERPNext for P&L data, or sync Mercury/PayPal transactions."
              action={
                <Link to="/admin" className="text-sm text-zt-primary hover:text-zt-accent">
                  Open Admin → integrations
                </Link>
              }
            />
          ) : (
            <BarChart
              data={revenueExpenseData}
              xKey="period"
              series={[
                { key: 'revenue', label: 'Revenue', color: CHART_THEME.revenue },
                { key: 'expenses', label: 'Expenses', color: CHART_THEME.expenses },
              ]}
            />
          )}
        </ChartCard>

        <ChartCard title="Cash flow" subtitle="Cumulative net from transaction sync">
          {chartsLoading ? (
            <ChartSkeleton />
          ) : cashFlowData.length === 0 ? (
            <EmptyState
              title="No cash flow data"
              message="Sync bank and payment integrations to populate cash flow."
              action={
                <Link to="/mercury" className="text-sm text-zt-primary hover:text-zt-accent">
                  Open Mercury sync
                </Link>
              }
            />
          ) : (
            <AreaChart data={cashFlowData} xKey="date" yKey="cumulative" />
          )}
        </ChartCard>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard title="Integration health" subtitle="Live adapter checks">
          {chartsLoading ? (
            <ChartSkeleton height={220} />
          ) : chartsError ? (
            <EmptyState title="Integration chart unavailable" message={chartsError} />
          ) : integrationSlices.length === 0 ? (
            <EmptyState
              title="No integrations configured"
              message="Set up adapters in Admin."
              action={
                <Link to="/admin" className="text-sm text-zt-primary hover:text-zt-accent">
                  Configure integrations
                </Link>
              }
            />
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-4 sm:justify-start">
              <div className="h-36 w-36 shrink-0">
                <DonutChart data={integrationSlices} height={144} />
              </div>
              <ul className="min-w-0 space-y-2.5 text-sm">
                {integrationSlices.map((slice) => (
                  <li key={slice.name} className="flex items-center gap-3">
                    <span className="flex min-w-0 items-center gap-2 text-zt-muted">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: slice.color }}
                        aria-hidden
                      />
                      <span className="truncate">{slice.name}</span>
                    </span>
                    <span className="shrink-0 tabular-nums font-semibold text-slate-200">
                      {slice.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ChartCard>

        <div className="min-w-0 lg:col-span-2">
          <ChartCard title="Recent activity" subtitle="Audit log (last 12 events)">
            {chartsLoading ? (
              <ChartSkeleton height={200} />
            ) : !charts?.recentActivity.length ? (
              <EmptyState title="No recent activity" message="Operator actions will appear in the audit log." />
            ) : (
              <div className="max-h-72 space-y-2 overflow-y-auto overscroll-y-contain pb-1 pr-1.5">
                {charts.recentActivity.map((event) => {
                  const detail = [event.actor, event.resourceType].filter(Boolean).join(' · ');
                  return (
                    <div
                      key={event.id}
                      className="flex min-w-0 items-center gap-3 rounded-lg border border-zt-border bg-zt-bg px-3 py-2.5 text-sm"
                    >
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div
                          className="truncate font-medium text-slate-200"
                          title={event.action}
                        >
                          {event.action}
                        </div>
                        {detail ? (
                          <div className="truncate text-xs text-zt-muted" title={detail}>
                            {detail}
                          </div>
                        ) : null}
                      </div>
                      <time className="shrink-0 whitespace-nowrap text-xs tabular-nums text-zt-muted">
                        {event.timestamp ? formatDateTime(event.timestamp) : '—'}
                      </time>
                    </div>
                  );
                })}
              </div>
            )}
          </ChartCard>
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <div className="min-w-0 rounded-xl border border-zt-border bg-zt-surface p-6">
            <div className="mb-4 flex min-w-0 flex-wrap items-center gap-2">
              <AlertCircle className="shrink-0 text-zt-warning" size={20} />
              <h2 className="text-lg font-semibold text-white">What needs attention</h2>
              {(summary?.pendingApprovals ?? 0) > 0 && (
                <span className="rounded-full bg-zt-danger/10 px-2 py-0.5 text-xs font-medium text-zt-danger">
                  {summary?.pendingApprovals} approvals
                </span>
              )}
            </div>

            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-14 animate-pulse rounded-lg bg-zt-border" />
                ))}
              </div>
            ) : summaryError ? (
              <EmptyState
                title="Attention items unavailable"
                message="Reconnect to the dashboard API to load operator alerts."
              />
            ) : attentionItems.length === 0 ? (
              <EmptyState
                title="All clear"
                message="No items need attention right now."
              />
            ) : (
              <div className="space-y-3">
                {attentionItems.map((item) => (
                  <AttentionItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0 space-y-4">
          <div className="min-w-0 rounded-xl border border-zt-border bg-zt-surface p-5">
            <h3 className="mb-1 font-semibold text-white">Quick links</h3>
            <p className="mb-3 text-sm text-zt-muted">
              Jump to integrated apps in a new tab.
            </p>
            <AppLinkGrid apps={quickLinkApps} />
          </div>

          <div className="rounded-xl border border-zt-border bg-zt-surface p-6">
            <h3 className="mb-3 font-semibold text-white">Quick actions</h3>
            <div className="grid grid-cols-1 gap-3">
              <QuickAction label="Open AI Console" to="/ai" />
              <QuickAction label="New invoice" to="/invoices" />
              <QuickAction label="Upload receipt" to="/receipts" />
              <QuickAction label="Review approvals" to="/approvals" />
              <QuickActionButton
                label="Sync Mercury"
                loading={mercurySyncing}
                onClick={handleMercurySync}
              />
            </div>
          </div>

          <AiInsightCard />
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 lg:grid-cols-2">
        <StatusWidgets />
        <div id="approval-queue" className="min-w-0">
          <ApprovalQueue compact />
          <p className="mt-2 text-right">
            <Link to="/approvals" className="text-xs text-zt-primary hover:underline">
              View all approvals
            </Link>
          </p>
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : summaryError ? (
          <>
            <StatCard label="Missing receipts" value="—" tone="neutral" />
            <StatCard label="Tax deadlines" value="—" tone="neutral" />
            <StatCard label="Backup health" value="—" tone="neutral" />
            <StatCard label="Last sync" value="—" tone="neutral" icon={<RefreshCw size={18} />} />
          </>
        ) : (
          <>
            <StatCard
              label="Missing receipts"
              value={String(summary?.missingReceipts ?? 0)}
              tone={(summary?.missingReceipts ?? 0) > 0 ? 'danger' : 'success'}
            />
            <StatCard
              label="Tax deadlines"
              value={String(summary?.taxDeadlines ?? 0)}
              tone={(summary?.taxDeadlines ?? 0) > 0 ? 'warning' : 'success'}
            />
            <StatCard
              label="Backup health"
              value={
                summary?.backupHealth === 'healthy'
                  ? 'Healthy'
                  : summary?.backupHealth === 'stale'
                  ? 'Stale'
                  : 'Needs attention'
              }
              tone={summary?.backupHealth === 'healthy' ? 'success' : 'danger'}
            />
            <StatCard
              label="Last sync"
              value={
                summary?.lastSyncAt
                  ? new Date(summary.lastSyncAt).toLocaleTimeString()
                  : 'Never'
              }
              tone="neutral"
              icon={<RefreshCw size={18} />}
            />
          </>
        )}
      </div>
    </div>
  );
}

function QuickAction({ label, to }: { label: string; to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg border border-zt-border bg-zt-bg p-3 text-sm font-medium text-slate-200 transition-colors hover:border-zt-primary hover:text-zt-primary"
    >
      {label}
      <ArrowUpRight size={16} />
    </Link>
  );
}

function QuickActionButton({
  label,
  onClick,
  loading,
}: {
  label: string;
  onClick: () => void;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className="flex w-full items-center justify-between rounded-lg border border-zt-border bg-zt-bg p-3 text-sm font-medium text-slate-200 transition-colors hover:border-zt-primary hover:text-zt-primary disabled:opacity-50"
    >
      {loading ? 'Syncing…' : label}
      <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
    </button>
  );
}

export default Overview;
